// START IMPORTS
// Context imports
import { AppConfig } from '@ltvco/refresh-lib/ctx';
// Payments imports
import { ReactivatePausedAccountCta } from '@ltvco/refresh-lib/payments';
// Theme imports
import {
  Text,
  Grid,
  Link,
  styled,
  CustomIcons,
  IconButton,
  Stack,
} from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  DateUtil,
  useScreenSize,
  useFirstRecordSearchRedirect,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  RightRailCard,
  NeedAssistanceSidebar,
  useSnackbar,
  closeSnackbar,
  EmailSettingsCard,
  MonitoredReportsModal,
  useAccount,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import { SideBarMigrationCta } from '@ltvco/refresh-lib/v2';
// Vendor imports
import CloseIcon from '@mui/icons-material/Close';
import { useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';

import { SidenavContainer } from '../components/sidenav/SidenavContainer';
// END IMPORTS

// START STYLED_COMPONENTS
const CreditCardIcon = styled(CustomIcons.CreditCardIcon)(({ theme }) => ({
  fontSize: '3.75rem',
  padding: theme.spacing(0.9375),
}));

const DollarsAndCoinsIcon = styled(CustomIcons.DollarsAndCoinsIcon)(
  ({ theme }) => ({
    fontSize: '3.75rem',
    padding: theme.spacing(0.9375),
  })
);

const StyledContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(2),
  width: '100%',
  '&&': {
    '& > *': {
      width: '100%',
      maxWidth: '100%',
      border: `1px solid ${theme.palette.background.light}`,
      boxShadow: 'none',
      '& > button': {
        width: '95%',
      },
      '& > div button': {
        paddingRight: 0,
      },
    },
  },
}));
// END STYLED_COMPONENTS

export function DashboardParent({
  contactUsHandler,
  handleLogout,
  openConfirmCancelModal,
}: {
  contactUsHandler: () => void;
  handleLogout: () => void;
  // eslint-disable-next-line no-unused-vars
  openConfirmCancelModal: (downsellTest?: boolean) => void;
}) {
  // START HOOKS_AND_QUERIES
  const { enqueueSnackbar } = useSnackbar();
  const { routingUtils } = useContext(AppConfig);
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useScreenSize();

  const { data: account } = useAccount(true);
  // END HOOKS_AND_QUERIES

  // START FIRST_RECORD_SEARCH
  const { showLoading: _showFirstReportLoading } = useFirstRecordSearchRedirect(
    {
      account,
      routingUtils,
      navigate,
    }
  );
  // END FIRST_RECORD_SEARCH

  // START STATE_MANAGEMENT
  const [monitoredReportsOpen, setMonitoredReportsOpen] = useState(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const dateUtil = new DateUtil();
  const currentDate = new Date();
  // END DERIVED_DATA_AND_UTILS

  // START EVENT_HANDLERS
  function closeMonitoredReportsModal() {
    setMonitoredReportsOpen(false);
  }

  function handleReactivationForPausedAccountsBanner() {
    if (!account) return;

    const isInPausedStatus = account?.meta?.subscription_state === 'paused';
    if (!isInPausedStatus) return;

    const today = currentDate.toISOString();
    const expirationDate = account?.account.subscription_info?.normalize_date;

    if (!expirationDate) return;

    const isAfterCurrentDate = dateUtil.isAfter(today, expirationDate);

    const expiredMessage = (
      <Text>
        <b>
          Your account expired on{' '}
          {dateUtil.parseDateFromString(
            account?.account.subscription_info?.normalize_date,
            'M/d/y'
          )}
          . Reactivate to regain access.
        </b>
      </Text>
    );

    function handleReactivationClick() {
      closeSnackbar(pausedAccountSnackbarId);
      navigate('/reactivation');
    }

    const cancelledMessage = (
      <Text>
        <b>
          Your account with member ID {account?.account.user_info.user_code} has
          been cancelled.
        </b>{' '}
        <br /> You have until{' '}
        {dateUtil.parseDateFromString(
          account?.account.subscription_info?.normalize_date,
          'M/d/y'
        )}{' '}
        to continue running reports. Please reactivate now to maintain access.
        Visit{' '}
        <Link onClick={handleReactivationClick} style={{ cursor: 'pointer' }}>
          here
        </Link>{' '}
        to reactivate.
      </Text>
    );

    const message = isAfterCurrentDate ? expiredMessage : cancelledMessage;

    const pausedAccountSnackbarId = enqueueSnackbar(
      <Stack direction="row" justifyContent="space-between">
        {message}
        {getCloseButton(() => {
          closeSnackbar(pausedAccountSnackbarId);
        })}
      </Stack>,
      {
        variant: 'info',
        persist: true,
        action: <></>,
      }
    );

    localStorage.setItem(
      'pausedAccountSnackbarId',
      JSON.stringify(pausedAccountSnackbarId)
    );
  }

  function getCloseButton(onClick: () => void) {
    return (
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={onClick}
        sx={{ marginLeft: 1 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    );
  }

  function handleSiteNotifications() {
    if (!account) return;
    const siteNotification = account?.meta?.notification;
    if (!siteNotification) return;
    const siteNotificationSnackbarId = enqueueSnackbar(
      <Text>
        <b>{siteNotification.title}</b> <br /> {siteNotification.message}
      </Text>,
      {
        variant: 'info',
        persist: true,
        action: (
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              closeSnackbar(siteNotificationSnackbarId);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      }
    );
  }
  // END EVENT_HANDLERS

  // START EFFECTS
  useEffect(() => {
    if (localStorage.getItem('pausedAccountSnackbarId')) {
      try {
        closeSnackbar(
          JSON.parse(localStorage.getItem('pausedAccountSnackbarId') || '')
        );
      } catch (e) {
        console.log('error', e);
      }
    }
    handleSiteNotifications();
    handleReactivationForPausedAccountsBanner();
  }, [account]);
  // END EFFECTS

  // START RENDER
  return (
    <SidenavContainer>
      <Grid container justifyContent="space-between">
        <Grid
          size={{
            xs: 12,
            md: 8,
            lg: 8,
          }}
          maxWidth={{ xs: '100%' }}
        >
          <Outlet />
        </Grid>

        <Grid
          container
          size={{ xs: 12, md: 3, lg: 3 }}
          spacing={1}
          direction="column"
          marginTop={2}
          maxWidth={{ xs: '100%', md: '299px' }}
        >
          {location.pathname.includes('/dashboard/billing') && (
            <Grid
              width={'100%'}
              order={{ xs: 6, md: 1 }}
              className="needs-assistance"
            >
              <NeedAssistanceSidebar
                logout={handleLogout}
                supportImgUrl={constants.images.img24hr}
                contactUsHandler={contactUsHandler}
                openConfirmCancelModal={openConfirmCancelModal}
              />
            </Grid>
          )}
          {location.pathname.includes('/dashboard/monitoring') && (
            <Grid width={'100%'} order={2}>
              <EmailSettingsCard
                currentInstantAlertsSetting={
                  account?.account?.alert_me.settings.instant_enabled || false
                }
                currentDigestAlertsSetting={
                  account?.account?.alert_me.settings.digest_enabled || false
                }
                handleMonitoredReports={() => setMonitoredReportsOpen(true)}
              />
              <MonitoredReportsModal
                isOpen={monitoredReportsOpen}
                onClose={() => closeMonitoredReportsModal()}
              />
            </Grid>
          )}

          {isMobile && (
            <Grid width={'100%'} order={2} key="mobile-sso-promo">
              <StyledContainer>
                <SideBarMigrationCta />
              </StyledContainer>
            </Grid>
          )}

          <Grid width={'100%'} order={3}>
            <RightRailCard
              icon={<DollarsAndCoinsIcon />}
              title="Check for Unclaimed Money"
              subtitle="Included with your membership is a state government search of billions in unclaimed money"
              body="Find out if unclaimed money is owed to you or anyone you know!"
              buttonText="Unclaimed Money Search"
              tagText="Bonus Feature"
              showTag={true}
              tagOnLeft={false}
              onButtonClick={() => navigate('unclaimed-money')}
            />
          </Grid>
          <Grid width={'100%'} order={4}>
            <RightRailCard
              icon={<CreditCardIcon />}
              title="Dark Web Scan"
              subtitle="Millions of online accounts are compromised each year"
              body="Search using your email address to see if you may have been compromised on the dark web"
              buttonText="Scan Now"
              tagText="New"
              showTag={true}
              tagOnLeft={false}
              onButtonClick={() => navigate('dark-web')}
            />
          </Grid>

          {account?.meta?.subscription_state === 'paused' && (
            <Grid order={5}>
              <ReactivatePausedAccountCta />
            </Grid>
          )}
        </Grid>
      </Grid>
    </SidenavContainer>
  );
  // END RENDER
}
